import { useCallback, useMemo, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useAuth, useOidc, usePermissions, useProfile } from "../../contexts";
import logo from "../../images/as-logo.png";
import logoSmall from "../../images/as-logo-sm2.png";
import { LogoutModal } from "../logout/Logout";
import { useAppSettingsQuery, usePersonQuery, useProfileQuery } from "../../queries";
import { Claims, ClaimsForAssigningRoles, Dashboard, getDashboardsForUser, UserTypes } from "../../models";
import { config } from "../../config";
import { TerritoryDropdown } from ".";
import { CreateSurgeryModal } from "..";

export function Header() {
    const { currentUser, impersonator, userIsLoading, userSummary } = useProfile();
    const {
        unimpersonateMutation: { mutate: unimpersonateUser },
    } = useProfileQuery();
    const { isAuthorized } = useAuth();
    const {
        myAppSettingsQuery: { data: appSettings },
    } = useAppSettingsQuery();
    const { hasPermission, hasAnyPermission, isSurgeryUser, hasRole } = usePermissions();
    const {
        meQuery: { data: myPersonRecord },
    } = usePersonQuery({ queryType: "me" });
    const { logout } = useOidc();

    const [showLogoutModal, setShowLogoutModal] = useState(false);
    const [showSurgeryModal, setShowSurgeryModal] = useState(false);

    const dashboardOptions = useMemo<Dashboard[]>(() => {
        const defaultDashboard: Dashboard = {
            name: "Dashboard",
            path: "/",
        };

        if (!userSummary) return [defaultDashboard];
        const dashboardsForUser = getDashboardsForUser(userSummary);
        if (!dashboardsForUser.length) return [defaultDashboard];
        return dashboardsForUser;
    }, [userSummary]);

    const userName = useMemo<string>(() => {
        return impersonator
            ? `${impersonator.firstName} as ${currentUser?.firstName} ${currentUser?.lastName}`
            : `${currentUser?.firstName ?? ""} ${currentUser?.lastName ?? ""}`;
    }, [currentUser, impersonator]);

    const logoSrc = useMemo<string>(() => {
        return appSettings?.logoUri?.length ? appSettings?.logoUri : logo;
    }, [appSettings]);

    const isSurgeonWithProfile = useMemo(
        () => hasRole(UserTypes.Surgeon) && !!myPersonRecord,
        [hasRole, myPersonRecord]
    );

    const handleLogout = useCallback(async () => {
        setShowLogoutModal(false);
        await logout();
    }, [logout]);

    const viewInternal = isAuthorized && hasPermission(Claims.ViewInternal);

    const viewAdminMenu =
        viewInternal &&
        hasAnyPermission([
            Claims.ViewUser,
            Claims.ViewUserGlobal,
            Claims.ViewAnyTenant,
            Claims.ViewChildTenant,
            ...ClaimsForAssigningRoles,
            Claims.ViewFormSelect,
            Claims.ViewFormSelectCategory,
            Claims.ViewAccount,
            Claims.EditNotificationConfig,
            Claims.ManageCountries,
            Claims.ManageStandardFees,
            Claims.ViewPerson,
            Claims.ManageShippingMethod,
            Claims.ViewSurgerySchedulerSurgeon,
            Claims.OfferTissue,
            Claims.ViewInvoice,
        ]);

    const viewAppSettings =
        viewInternal && hasAnyPermission([Claims.EditAnyTenantAppSettings, Claims.EditCurrentTenantAppSettings]);

    return (
        <>
            <div className="keysys-header">
                <Navbar collapseOnSelect expand="lg">
                    <Container fluid={isAuthorized}>
                        <div className="d-flex align-items-center">
                            <Navbar.Brand style={{ marginRight: "0" }}>
                                <Link to="/">
                                    <img
                                        alt={`${config.APP_NAME} logo`}
                                        src={logoSrc}
                                        style={{ maxWidth: "200px", maxHeight: "29px" }}
                                        className="d-none d-lg-block"
                                    />
                                    <img
                                        alt={`${config.APP_NAME} logo`}
                                        src={logoSmall}
                                        style={{ maxWidth: "29px", maxHeight: "29px" }}
                                        className="d-lg-none"
                                    />
                                </Link>
                            </Navbar.Brand>

                            {viewInternal && <TerritoryDropdown isMobile />}
                        </div>
                        <Navbar.Toggle aria-controls="navbarScroll" />
                        <Navbar.Collapse id="responsive-navbar-nav" className="justify-content-between">
                            {!userIsLoading && (
                                <>
                                    {isAuthorized && (
                                        <Nav>
                                            {dashboardOptions.length === 1 ? (
                                                <Nav.Link as={Link} to={`dashboard/${dashboardOptions[0].path}`}>
                                                    My Dashboard
                                                </Nav.Link>
                                            ) : (
                                                <>
                                                    <Nav.Link
                                                        as={Link}
                                                        to="/"
                                                        className="header-item"
                                                        eventKey="my-dashboard"
                                                    >
                                                        My Dashboard
                                                    </Nav.Link>

                                                    <NavDropdown title="Dashboards" className="header-item">
                                                        {dashboardOptions.map((dashboard) => (
                                                            <NavDropdown.Item
                                                                key={`dashboard-${dashboard.name}`}
                                                                as={Link}
                                                                to={`/dashboard/${dashboard.path}`}
                                                                eventKey={dashboard.name}
                                                            >
                                                                {dashboard.name}
                                                            </NavDropdown.Item>
                                                        ))}
                                                    </NavDropdown>
                                                </>
                                            )}

                                            {viewInternal && <TerritoryDropdown />}

                                            {isSurgeryUser && (
                                                <>
                                                    <Nav.Link
                                                        className="header-item"
                                                        eventKey="new-surgery"
                                                        onClick={() => setShowSurgeryModal(true)}
                                                    >
                                                        New Surgery
                                                    </Nav.Link>

                                                    <Nav.Link
                                                        as={Link}
                                                        to="/surgery-calendar"
                                                        className="header-item"
                                                        eventKey="surgery-calendar"
                                                    >
                                                        Calendar
                                                    </Nav.Link>
                                                </>
                                            )}
                                        </Nav>
                                    )}

                                    <Nav>
                                        {viewInternal && (
                                            <>
                                                <Nav.Link
                                                    as={Link}
                                                    to="dashboard/shipments"
                                                    className="header-item"
                                                    eventKey="shipments"
                                                >
                                                    Shipments
                                                </Nav.Link>
                                                {/* TODO: red badge for to be shipped or incoming today */}

                                                <NavDropdown title="Search" className="header-item">
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/search?tab=cases"
                                                        eventKey="search-cases"
                                                    >
                                                        Cases
                                                    </NavDropdown.Item>
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/search?tab=tissues"
                                                        eventKey="search-tissues"
                                                    >
                                                        Tissues
                                                    </NavDropdown.Item>
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/search?tab=orders"
                                                        eventKey="search-orders"
                                                    >
                                                        Orders
                                                    </NavDropdown.Item>
                                                    {hasPermission(Claims.ViewInvoice) && (
                                                        <NavDropdown.Item
                                                            as={Link}
                                                            to="/search?tab=invoices"
                                                            eventKey="search-invoices"
                                                        >
                                                            Invoices
                                                        </NavDropdown.Item>
                                                    )}
                                                </NavDropdown>
                                            </>
                                        )}

                                        {viewAdminMenu && (
                                            <NavDropdown title="Admin" className="header-item">
                                                {hasAnyPermission([Claims.ViewUser, Claims.ViewUserGlobal]) && (
                                                    <NavDropdown.Item as={Link} to="/users" eventKey="users">
                                                        Users
                                                    </NavDropdown.Item>
                                                )}
                                                {hasPermission(Claims.ViewSurgerySchedulerSurgeon) && (
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/surgery-scheduler-surgeons"
                                                        eventKey="surgery-scheduler-surgeons"
                                                    >
                                                        Surgery Schedulers
                                                    </NavDropdown.Item>
                                                )}
                                                {hasAnyPermission(ClaimsForAssigningRoles) && (
                                                    <NavDropdown.Item as={Link} to="/roles" eventKey="roles">
                                                        Role Permissions
                                                    </NavDropdown.Item>
                                                )}
                                                {hasAnyPermission([Claims.ViewAnyTenant, Claims.ViewChildTenant]) && (
                                                    <NavDropdown.Item as={Link} to="/tenants" eventKey="tenants">
                                                        Tenants
                                                    </NavDropdown.Item>
                                                )}

                                                {hasPermission(Claims.EditNotificationConfig) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item
                                                            as={Link}
                                                            to="/notification-settings"
                                                            eventKey="notification-settings"
                                                        >
                                                            Notification Settings
                                                        </NavDropdown.Item>
                                                    </>
                                                )}

                                                {hasPermission(Claims.ViewAccount) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item as={Link} to="/accounts" eventKey="accounts">
                                                            Accounts
                                                        </NavDropdown.Item>
                                                        <NavDropdown.Item as={Link} to="/offices" eventKey="offices">
                                                            Offices
                                                        </NavDropdown.Item>
                                                    </>
                                                )}

                                                {hasPermission(Claims.ManageCountries) && (
                                                    <NavDropdown.Item as={Link} to="/countries" eventKey="countries">
                                                        Countries
                                                    </NavDropdown.Item>
                                                )}

                                                {hasPermission(Claims.ManageStandardFees) && (
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/standard-fees"
                                                        eventKey="standard-fees"
                                                    >
                                                        Fees
                                                    </NavDropdown.Item>
                                                )}

                                                {hasPermission(Claims.ViewPerson) && (
                                                    <NavDropdown.Item as={Link} to="/people" eventKey="people">
                                                        People
                                                    </NavDropdown.Item>
                                                )}

                                                {hasPermission(Claims.ManageShippingMethod) && (
                                                    <NavDropdown.Item
                                                        as={Link}
                                                        to="/shipping-methods"
                                                        eventKey="shipping-methods"
                                                    >
                                                        Shipping Methods
                                                    </NavDropdown.Item>
                                                )}

                                                {hasPermission(Claims.OfferTissue) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item
                                                            as={Link}
                                                            to="/tissue-offer-log"
                                                            eventKey="tissue-offer-log"
                                                        >
                                                            Tissue Offer Log
                                                        </NavDropdown.Item>
                                                    </>
                                                )}

                                                {hasPermission(Claims.ViewInvoice) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item as={Link} to="/qbo" eventKey="quickbooks">
                                                            QuickBooks
                                                        </NavDropdown.Item>
                                                    </>
                                                )}

                                                {hasRole(UserTypes.GlobalAdmin) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item as={Link} to="/labkey" eventKey="labkey">
                                                            LabKey
                                                        </NavDropdown.Item>
                                                    </>
                                                )}

                                                {hasPermission(Claims.ViewFormSelect) && (
                                                    <>
                                                        <NavDropdown.Divider />
                                                        <NavDropdown.Item
                                                            as={Link}
                                                            to="/form-dropdowns"
                                                            eventKey="form-dropdowns"
                                                        >
                                                            Form Dropdowns
                                                        </NavDropdown.Item>
                                                    </>
                                                )}
                                            </NavDropdown>
                                        )}

                                        {hasRole(UserTypes.SurgeryScheduler) && (
                                            <Nav.Link
                                                as={Link}
                                                to="/my-surgeons"
                                                className="header-item"
                                                eventKey="my-surgeons"
                                            >
                                                My Surgeons
                                            </Nav.Link>
                                        )}

                                        {isSurgeonWithProfile && (
                                            <Nav.Link
                                                as={Link}
                                                to={`/people/${myPersonRecord?.id}`}
                                                className="header-item"
                                                eventKey="surgeon-preferences"
                                            >
                                                Preferences
                                            </Nav.Link>
                                        )}

                                        {isAuthorized && (
                                            <>
                                                <NavDropdown title={userName} className="header-item d-none d-lg-block">
                                                    {!!impersonator && (
                                                        <NavDropdown.Item
                                                            onClick={unimpersonateUser}
                                                            eventKey="unimpersonate"
                                                        >
                                                            Cancel Impersonation
                                                        </NavDropdown.Item>
                                                    )}
                                                    <NavDropdown.Item as={Link} to="/profile" eventKey="profile">
                                                        Profile
                                                    </NavDropdown.Item>
                                                    {viewAppSettings && (
                                                        <NavDropdown.Item
                                                            as={Link}
                                                            to="/app-settings"
                                                            eventKey="app-settings"
                                                        >
                                                            App Settings
                                                        </NavDropdown.Item>
                                                    )}
                                                    <NavDropdown.Item
                                                        onClick={() => setShowLogoutModal(true)}
                                                        eventKey="logout"
                                                    >
                                                        Log Out
                                                    </NavDropdown.Item>
                                                </NavDropdown>

                                                <span className="d-block d-lg-none">
                                                    <hr />
                                                    <Nav.Link as={Link} to="/profile" eventKey="profile">
                                                        Profile
                                                    </Nav.Link>

                                                    {viewAppSettings && (
                                                        <Nav.Link as={Link} to="/app-settings" eventKey="app-settings">
                                                            App Settings
                                                        </Nav.Link>
                                                    )}

                                                    <Nav.Link
                                                        onClick={() => setShowLogoutModal(true)}
                                                        eventKey="logout"
                                                    >
                                                        Log Out
                                                    </Nav.Link>
                                                </span>
                                            </>
                                        )}
                                    </Nav>
                                </>
                            )}
                        </Navbar.Collapse>
                    </Container>
                </Navbar>
            </div>

            {showLogoutModal && <LogoutModal onCancel={() => setShowLogoutModal(false)} onLogout={handleLogout} />}
            {showSurgeryModal && (
                <CreateSurgeryModal onHide={() => setShowSurgeryModal(false)} show={showSurgeryModal} />
            )}
        </>
    );
}
